import React, { ReactNode, useContext } from "react";
import Grid from "../Grid/Grid";
import GridColumn from "../Grid/GridColumn";
import { defineMessages, useIntl } from "react-intl";
import PageHeader from "../PageHeader/PageHeader";
import { Pagetype } from "../../interfaces/Config";
import { Classroom, UserType } from "../../interfaces/User";
import ContainerBlock from "../../design-system-components/ContainerBlock/ContainerBlock";
import { Button } from "@evidenceb/athena-common/design-system/Button";
import ModuleList from "../../pages/DashBoard/DashboardTeacher/Classroom/Details/ModuleList/ModuleList";
import { IGroup } from "../../pages/DashBoard/DashboardTeacher/Group/types";
import { LegacyLoader } from "../Loader/Loader";
import { useUrl } from "../../hooks/usePage";
import { useModuleLockStatus } from "../../hooks/useLockStatus__legacy";
import UserList from "../../pages/DashBoard/DashboardTeacher/Classroom/Details/UserList/UserList";
import UsersCodeDocument from "../UsersCodePrinting/UsersCodeDocument/UsersCodeDocument";
import { createPortal } from "react-dom";
import { configStore } from "../../contexts/ConfigContext";
import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { LocationState } from "../Player/Header/type";
import useOnlineStatus from "../../hooks/useOnlineStatus";
import { useHasWorkmode } from "../../hooks/useWorkmode";

import "./StudentCollectionDetails.scss";
import { WorkModeType } from "@evidenceb/athena-common/interfaces/Workmode";

export type StudentCollectionDetailsProps = {
    collection: Classroom | IGroup;
    type: "group" | "classroom";
    title: ReactNode;
    className?: string;
    studentsListOptions?: React.ReactNode;
    studentsListFooter?: React.ReactNode;
};

const StudentCollectionDetails = ({
    collection,
    type,
    title,
    studentsListOptions,
    studentsListFooter,
    className,
}: StudentCollectionDetailsProps) => {
    const intl = useIntl();
    const isOnline = useOnlineStatus();
    const {
        config: { features },
    } = useContext(configStore);
    const dashboardUrl = useUrl(Pagetype.DASHBOARD);
    const location = useLocation();
    const { from } = (location.state as LocationState) || {};
    const { lockedModules, unlockedModules } = useModuleLockStatus({
        collectionId: collection.id,
        type,
    });
    const hasSoloAIMode = useHasWorkmode(WorkModeType.SoloAI, UserType.Student);

    return (
        <>
            <div
                id="student-collection-details"
                className={classNames("student-collection-details", className)}
            >
                <main>
                    <Grid>
                        <GridColumn col={12}>
                            <PageHeader
                                className="student-collection-details__header"
                                title={{ content: title }}
                                backTo={from ?? dashboardUrl}
                            />
                        </GridColumn>
                    </Grid>
                </main>
                <aside>
                    <Grid>
                        {hasSoloAIMode && (
                            <GridColumn
                                col={8}
                                className="student-collection-details__modules"
                            >
                                <ContainerBlock>
                                    <div className="student-collection-details__modules__header">
                                        <h2>
                                            {intl.formatMessage(
                                                messages.enabledModules
                                            )}
                                        </h2>
                                        {type === "classroom" && isOnline && (
                                            <Button
                                                type="button"
                                                label={intl.formatMessage(
                                                    messages.PRMLabel
                                                )}
                                                asRouterLink={{
                                                    to: `/prm/${collection.id}`,
                                                }}
                                            />
                                        )}
                                    </div>

                                    {status === "loading" ? (
                                        <div className="student-collection-details__module-list">
                                            <LegacyLoader />
                                        </div>
                                    ) : (
                                        <ModuleList
                                            type={type}
                                            modules={unlockedModules}
                                            collectionId={collection.id}
                                            students={collection.students.map(
                                                (s) => s.id
                                            )}
                                        />
                                    )}
                                </ContainerBlock>
                                {lockedModules.length > 0 && (
                                    <ContainerBlock>
                                        <div className="student-collection-details__modules__header">
                                            <h2>
                                                {intl.formatMessage(
                                                    messages.disabledModules
                                                )}
                                            </h2>
                                            {type === "classroom" &&
                                                isOnline && (
                                                    <Button
                                                        type="button"
                                                        label={intl.formatMessage(
                                                            messages.PRMLabel
                                                        )}
                                                        asRouterLink={{
                                                            to: `/prm/${collection.id}`,
                                                        }}
                                                    />
                                                )}
                                        </div>
                                        <ModuleList
                                            type={type}
                                            modules={lockedModules}
                                            collectionId={collection.id}
                                            students={collection.students.map(
                                                (s) => s.id
                                            )}
                                        />
                                    </ContainerBlock>
                                )}
                            </GridColumn>
                        )}
                        <GridColumn col={hasSoloAIMode ? 4 : 12}>
                            <UserList
                                classroomId={collection.id}
                                type={type}
                                optionsChildren={studentsListOptions}
                                footerChildren={studentsListFooter}
                            />
                        </GridColumn>
                    </Grid>
                </aside>
            </div>

            {isOfTypeClassroom(collection) &&
                features.studentManagement &&
                createPortal(
                    <UsersCodeDocument
                        collection={collection}
                        className="users-code-document-to-print"
                    />,
                    document.body
                )}
        </>
    );
};

export default StudentCollectionDetails;

const isOfTypeClassroom = (
    collection: Classroom | IGroup
): collection is Classroom => {
    return "code" in collection;
};

const messages = defineMessages({
    classroomCode: {
        id: "classDetails-code",
        defaultMessage: "Class code:",
    },
    PRMLabel: {
        id: "classList-settingPRMLabel",
        defaultMessage: "Manage resources",
    },
    enabledModules: {
        id: "classroom-details-enabledModules",
        defaultMessage: "Enabled modules",
    },
    disabledModules: {
        id: "classroom-details-disabledModules",
        defaultMessage: "Disabled modules",
    },
    renameGroup: {
        id: "classList-renameGroup",
        defaultMessage: "Rename group",
    },
    addStudents: {
        id: "classroom-details-addStudents",
        defaultMessage: "Add students",
    },
    connectStudents: {
        id: "classroom-details-connectStudents",
        defaultMessage: "Connect my students ?",
    },
});
